/* eslint-disable jsdoc/require-jsdoc */
/* eslint-disable no-nested-ternary */
import { ReactElement, useState, useEffect } from 'react';
import clsx from 'clsx';
import { Map, MapMouseEvent, useMap, AdvancedMarker } from '@vis.gl/react-google-maps';
import { useFormContext, Controller } from 'react-hook-form';
import {
  DeviceLocation,
  DeviceType,
  ExtendedDeviceAttributes
} from '../../constants/telosair-interfaces';
import Button from '../../controls/Button';

type RHFMapProps = {
  name: string;
  selectedDevices: ExtendedDeviceAttributes[];
  defaultCenter: string;
  newDevicesWithLocation: DeviceLocation;
  setNewDevicesWithLocation: (data: DeviceLocation) => void;
  handleSelect: (id: number, selectionStatus: boolean) => void;
};

type DeviceMarker = {
  id: number;
  deviceName: string;
  position: { lat: number; lng: number };
  isMarked: boolean;
};

/**
 * A React Hook Form compatible Google Maps component for marking device locations.
 * @param props - Properties of RHFMap component.
 * @returns - A Map Component with device markers.
 */
export default function RHFMap(props: RHFMapProps): ReactElement {
  const {
    name,
    selectedDevices,
    defaultCenter,
    newDevicesWithLocation,
    handleSelect,
    setNewDevicesWithLocation
  } = props;
  const { control, setValue, watch } = useFormContext();
  const [latitude, longitude] = defaultCenter.replace(/[()]/g, '').split(',');
  const [activeDevice, setActiveDevice] = useState<number | null>(null);
  const [inputLat, setInputLat] = useState<string>('');
  const [inputLng, setInputLng] = useState<string>('');
  const map = useMap();

  const markedLocation = watch('markedLocation') || {};

  useEffect(() => {
    if (activeDevice && newDevicesWithLocation[activeDevice]) {
      const { x, y } = newDevicesWithLocation[activeDevice];
      setInputLat(y.toString());
      setInputLng(x.toString());
    }
  }, [activeDevice, newDevicesWithLocation]);

  const handleMapClick = (e: MapMouseEvent, onChange: (value: DeviceMarker[]) => void): void => {
    if (!activeDevice) return;

    const lat = e.detail.latLng?.lat;
    const lng = e.detail.latLng?.lng;

    if (lat !== undefined && lng !== undefined) {
      const device = selectedDevices.find((d) => d.id === activeDevice);
      if (!device) return;

      const updatedLocations: DeviceLocation = {
        ...newDevicesWithLocation,
        [activeDevice]: { x: lng, y: lat, deviceType: DeviceType.OUTDOOR }
      };

      setNewDevicesWithLocation(updatedLocations);
      setActiveDevice(null);
      onChange(
        Object.entries(updatedLocations).map(([id, pos]) => ({
          id: Number(id),
          deviceName: selectedDevices.find((d) => d.id === Number(id))?.deviceName || '',
          position: { lat: pos.y, lng: pos.x },
          isMarked: true
        }))
      );

      setValue('markedLocation', {
        ...markedLocation,
        [activeDevice]: { lat, lng }
      });
    }
  };

  const handleMarkerDrag = (
    e: any,
    currentMarker: DeviceMarker,
    onChange: (value: DeviceMarker[]) => void
  ): void => {
    const lat = e.latLng?.lat();
    const lng = e.latLng?.lng();
    if (lat !== undefined && lng !== undefined) {
      const updatedLocations: DeviceLocation = {
        ...newDevicesWithLocation,
        [currentMarker.id]: { x: lng, y: lat, deviceType: DeviceType.OUTDOOR }
      };

      setNewDevicesWithLocation(updatedLocations);
      onChange(
        Object.entries(updatedLocations).map(([id, pos]) => ({
          id: Number(id),
          deviceName: selectedDevices.find((d) => d.id === Number(id))?.deviceName || '',
          position: { lat: pos.y, lng: pos.x },
          isMarked: true
        }))
      );

      setValue('markedLocation', {
        ...markedLocation,
        [currentMarker.id]: { lat, lng }
      });
    }
  };

  const handleDeviceSelect = (device: ExtendedDeviceAttributes): void => {
    setActiveDevice(device.id);

    const existingLocation = newDevicesWithLocation[device.id];

    if (existingLocation && map) {
      map.panTo({ lat: existingLocation.y, lng: existingLocation.x });
    } else if (map) {
      const center = map.getCenter();
      if (center) {
        const lat = center.lat();
        const lng = center.lng();

        const updatedLocations: DeviceLocation = {
          ...newDevicesWithLocation,
          [device.id]: { x: lng, y: lat, deviceType: DeviceType.OUTDOOR }
        };

        setNewDevicesWithLocation(updatedLocations);
        setValue(
          name,
          Object.entries(updatedLocations).map(([id, pos]) => ({
            id: Number(id),
            deviceName: selectedDevices.find((d) => d.id === Number(id))?.deviceName || '',
            position: { lat: pos.y, lng: pos.x },
            isMarked: true
          }))
        );

        setValue('markedLocation', {
          ...markedLocation,
          [device.id]: { lat, lng, deviceType: DeviceType.OUTDOOR }
        });
      }
    }
  };

  const handleApply = (onChange: (value: DeviceMarker[]) => void): void => {
    if (!activeDevice) return;

    const lat = parseFloat(inputLat);
    const lng = parseFloat(inputLng);

    if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
      const updatedLocations: DeviceLocation = {
        ...newDevicesWithLocation,
        [activeDevice]: { x: lng, y: lat }
      };

      setNewDevicesWithLocation(updatedLocations);
      onChange(
        Object.entries(updatedLocations).map(([id, pos]) => ({
          id: Number(id),
          deviceName: selectedDevices.find((d) => d.id === Number(id))?.deviceName || '',
          position: { lat: pos.y, lng: pos.x },
          isMarked: true
        }))
      );

      setValue('markedLocation', {
        ...markedLocation,
        [activeDevice]: { lat, lng }
      });

      // Show the marked location on the map
      if (map) {
        map.panTo({ lat, lng });
      }
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        validate: () => {
          return (
            Object.keys(newDevicesWithLocation).length === selectedDevices.length ||
            'Please mark all devices on the map and click apply'
          );
        }
      }}
      render={({ field: { onChange }, fieldState: { error } }) => (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {selectedDevices.map((device) => {
              const isMarked = device.id in newDevicesWithLocation;
              const isActive = activeDevice === device.id;
              const lat = isMarked ? newDevicesWithLocation[device.id].y.toString() : '';
              const lng = isMarked ? newDevicesWithLocation[device.id].x.toString() : '';
              return (
                <div className="w-full" key={device.id}>
                  <div className="text-sm font-semibold text-white">{device.deviceName}</div>
                  <div className="flex flex-row items-center gap-2 mt-1">
                    <input
                      type="text"
                      placeholder="Lat"
                      value={isActive ? inputLat : lat}
                      onFocus={() => handleDeviceSelect(device)}
                      onChange={(e) => setInputLat(e.target.value)}
                      className="px-2 h-10 w-28 border rounded  bg-transparent text-gray-300 border-gray-700 bg-container-500 focus:dark:border-primary-300 focus-within:dark:ring-transparent"
                    />
                    <input
                      type="text"
                      placeholder="Lng"
                      value={isActive ? inputLng : lng}
                      onFocus={() => handleDeviceSelect(device)}
                      onChange={(e) => setInputLng(e.target.value)}
                      className="px-2 w-28 h-10 border rounded  bg-transparent text-gray-300 border-gray-700 bg-container-500 focus:dark:border-primary-300 focus-within:dark:ring-transparent"
                    />
                    <Button
                      size="sm"
                      theme="secondary-success"
                      name="Apply"
                      onClick={() => handleApply(onChange)}
                      className="!px-2 h-10 !min-w-[50px] rounded-lg border transition-colors font-semibold"
                    />
                    <Button
                      size="sm"
                      theme="danger"
                      name="Remove"
                      onClick={() => handleSelect(device.id, false)}
                      className="!px-2 h-10 !min-w-[50px] rounded-lg border transition-colors font-semibold"
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div
            className={clsx(
              'grid h-[20rem] p-2 rounded-lg border',
              error ? '!border-error-500' : 'border-gray-700 dark:border-gray-700'
            )}
          >
            <Map
              mapId="2710c5f22fa0158b"
              style={{ flexGrow: '1' }}
              defaultCenter={{ lat: parseFloat(latitude), lng: parseFloat(longitude) }}
              defaultZoom={15}
              gestureHandling="auto"
              scrollwheel
              draggable
              zoomControl
              disableDefaultUI
              onClick={(e) => handleMapClick(e, onChange)}
            >
              {Object.entries(newDevicesWithLocation).map(([id, position]) => {
                const device = selectedDevices.find((d) => d.id === Number(id));
                if (!device) return null;
                return (
                  <div key={id}>
                    <AdvancedMarker
                      position={{ lat: position.y, lng: position.x }}
                      title={device.deviceName}
                      draggable
                      onDragEnd={(e) =>
                        handleMarkerDrag(
                          e,
                          {
                            id: Number(id),
                            deviceName: device.deviceName,
                            position: { lat: position.y, lng: position.x },
                            isMarked: true
                          },
                          onChange
                        )
                      }
                    >
                      <div className="flex flex-col items-center">
                        <div
                          className={clsx(
                            'bg-primary-500 rounded-full w-6 h-6',
                            activeDevice === Number(id)
                              ? 'animate-pulse border-2 border-x-success-900'
                              : ''
                          )}
                        />
                        <div className="text-base mt-1 px-2 py-1 shadow-lg font-bold bg-white inline-block whitespace-nowrap">
                          {device.deviceName}
                        </div>
                      </div>
                    </AdvancedMarker>
                  </div>
                );
              })}
            </Map>
          </div>

          {error && <p className="text-sm text-error-500">{error.message}</p>}
        </div>
      )}
    />
  );
}
